import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material'
import { t } from 'i18next'
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { useBlocker, useLocation, useNavigate } from 'react-router-dom'
import { useConfirmedLogout, useEditMode, useLoggingOutWarning } from '../../store/redux/hooks'
import { normalizePathname } from '../../store/utils/normalizePathname'
import ConfirmButton from '../Button/ConfirmButton'
import { BaseDialogProps } from './BaseDialogProps'

type DiscardChangesDialogProps = BaseDialogProps & {
  checkUnsavedChanges: () => boolean
  resetForm?: () => void
  setIsCancelDialogOpen: Dispatch<SetStateAction<boolean>>
  disableBlocker?: boolean
  actionOnClose?: () => void
  actionOnConfirm?: () => void
}

const DiscardChangesDialog = ({
  checkUnsavedChanges,
  resetForm,
  open,
  setIsCancelDialogOpen,
  disableBlocker = false,
  actionOnClose,
  actionOnConfirm,
}: DiscardChangesDialogProps) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { setIsConfirmedLogout } = useConfirmedLogout()
  const { setIsEditMode } = useEditMode()
  const { isLoggingOutWarning, setIsLoggingOutWarning } = useLoggingOutWarning()
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  const blocker = useBlocker(() => {
    if (!disableBlocker && checkUnsavedChanges()) {
      setIsCancelDialogOpen(true)
      return true
    }
    setIsEditMode(false)
    return false
  })

  const onClose = () => {
    setIsCancelDialogOpen(false)
    setIsLoggingOut(false)
    if (actionOnClose) {
      actionOnClose()
    }
  }

  const handleConfirmCancel = () => {
    if (isLoggingOut) handleConfirmLogout()
    else setIsEditMode(false)
    onClose()
    if (actionOnConfirm) {
      actionOnConfirm()
    }
    if (blocker.state === 'blocked') {
      blocker.proceed()
      if (normalizePathname(location.pathname) === normalizePathname(blocker.location.pathname)) {
        navigate(0)
      }
    } else {
      if (resetForm) {
        resetForm()
      }
    }
  }

  const handleDialogCancel = () => {
    setIsLoggingOut(false)
    if (isLoggingOutWarning) handleCancelLogout()
    onClose()
    if (blocker.state === 'blocked') {
      blocker.reset()
    }
  }

  const handleConfirmLogout = useCallback(() => {
    setIsConfirmedLogout(true)
    setIsLoggingOutWarning(false)
    setIsEditMode(false)
  }, [setIsConfirmedLogout, setIsEditMode, setIsLoggingOutWarning])

  const handleCancelLogout = () => {
    setIsConfirmedLogout(false)
    setIsLoggingOutWarning(false)
    setIsLoggingOut(false)
  }

  useEffect(() => {
    if (isLoggingOutWarning) {
      if (checkUnsavedChanges()) {
        setIsCancelDialogOpen(true)
        setIsLoggingOut(true)
        setIsLoggingOutWarning(false)
      } else {
        handleConfirmLogout()
      }
    }
  }, [checkUnsavedChanges, handleConfirmLogout, isLoggingOutWarning, setIsCancelDialogOpen, setIsLoggingOutWarning])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableRestoreFocus
      PaperProps={{
        sx: {
          paddingLeft: 1,
          paddingRight: 2,
          paddingTop: 1,
          paddingBottom: 2,
        },
      }}
    >
      <DialogTitle>{t('page.usersProfile.dialog.cancel.title')}</DialogTitle>
      <DialogContent>
        <Stack direction="column">
          <Typography component="span">{t('page.usersProfile.dialog.cancel.content')}</Typography>
          <Typography component="span" sx={{ fontStyle: 'italic', pt: 1, color: 'warning.main' }}>
            {t('page.usersProfile.dialog.cancel.warning')}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ gap: 2 }}>
        <Button size="small" onClick={handleDialogCancel}>
          {t('page.usersProfile.dialog.return')}
        </Button>
        <ConfirmButton text={t('page.usersProfile.dialog.cancel.confirm')} onClick={handleConfirmCancel} />
      </DialogActions>
    </Dialog>
  )
}

export default DiscardChangesDialog
